import { useRef, useState } from "react";
import { FileCheck2, Loader2, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../../lib/api";

export default function RabiesUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/waivers/vaccination", fd);
      onChange({ path: data.path, filename: data.filename || file.name });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="rabies-upload" className="rounded-lg border border-lavender-700/10 bg-white p-5">
      <p className="text-sm font-medium text-stone-700">Rabies vaccination record</p>
      <p className="mt-1 text-xs text-stone-500">Upload a PDF, take a photo or add a screenshot of your dog's current rabies certificate. Optional, but it saves time at check-in.</p>
      <input ref={inputRef} type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" className="hidden" onChange={onFile} data-testid="rabies-file-input" />
      {value ? (
        <div data-testid="rabies-uploaded" className="mt-4 flex items-center justify-between gap-3 rounded-md bg-grass-100/70 px-4 py-3 text-sm text-grass-700">
          <span className="flex items-center gap-2 truncate"><FileCheck2 size={16} className="shrink-0" /><span className="truncate">{value.filename}</span></span>
          <button type="button" onClick={() => onChange(null)} data-testid="rabies-remove-button" aria-label="Remove file" className="grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors hover:bg-grass-500/20">
            <X size={14} />
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="rabies-upload-button"
            className="btn-secondary mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {busy ? "Uploading…" : "Upload PDF, photo or screenshot"}
          </button>
          <p data-testid="rabies-reminder" className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
            <strong>No record handy?</strong> No problem, but please bring proof of current rabies vaccination on the day of the appointment.
            If rabies vaccination cannot be provided, we will not be able to provide services.
          </p>
        </>
      )}
      {error && <p data-testid="rabies-error" className="mt-3 text-xs text-red-700">{error}</p>}
    </div>
  );
}
